import type { ApiClient } from '@carbonwebmail/api-client'
import type { CampaignDto } from '@carbonwebmail/contracts'
import { count } from '../../ui/format.js'
import type { PillTone } from '../../ui/StatusPill.js'

/**
 * Wording and colour for a campaign's state.
 *
 * Keyed by the contract's own union rather than by `string`, so every lookup is
 * total and adding a state to the contract fails the build here until someone
 * decides what it should say. A `Record<string, …>` would quietly render
 * nothing for it.
 *
 * `queued` and `sending` are the same tone deliberately. The difference between
 * them is whether a worker has reached the first row, which is not a difference
 * the person who pressed send can act on — both mean "it is going out".
 */
export const CAMPAIGN_STATUS: Record<
  CampaignDto['status'],
  { readonly label: string; readonly tone: PillTone }
> = {
  draft: { label: 'draft', tone: 'neutral' },
  // In the column and unreachable: scheduling is not built, so no campaign is
  // ever in this state today.
  scheduled: { label: 'scheduled', tone: 'neutral' },
  queued: { label: 'queued', tone: 'warn' },
  sending: { label: 'sending', tone: 'warn' },
  paused: { label: 'paused', tone: 'neutral' },
  sent: { label: 'sent', tone: 'ok' },
  failed: { label: 'failed', tone: 'danger' },
}

/**
 * Where a campaign's name leads.
 *
 * A draft opens in the composer and everything else opens its report, because
 * that is what each one is for: a draft has nothing to report — its report says
 * "not sent yet" — and a sent campaign cannot be rewritten. Sending somebody
 * from a draft to an empty report is how they conclude there is no way to write
 * a campaign at all.
 *
 * Here rather than in each screen because three lists link to a campaign — the
 * table, the calendar, and the shell's search — and three copies of this
 * decision is two that will not be updated when it changes.
 */
export function campaignHome(campaign: CampaignDto): string {
  return campaign.status === 'draft'
    ? `/campaigns/${campaign.campaignId}/edit`
    : `/campaigns/${campaign.campaignId}`
}

/**
 * Who a campaign goes to, in words.
 *
 * A null segment is the whole subscribed list, and saying "Everyone subscribed"
 * is the honest reading of it — an empty cell would look like a campaign with
 * no audience, which is a different and much worse thing.
 */
export function audienceOf(campaign: CampaignDto): string {
  return campaign.segmentName ?? 'Everyone subscribed'
}

/**
 * What to say after asking for a campaign to be queued.
 *
 * Here rather than beside the screen that sends, because two screens read these
 * outcomes — the review that sends, and anything that later reports on a send —
 * and a second copy of "already queued is not a failure" is a copy that
 * eventually says it is.
 *
 * `already-queued` is the one worth being careful about. Somebody pressed
 * twice, or their first request timed out after it had already worked; what
 * they need is to be told the send is under way, not that they did something
 * wrong.
 */
export function queueNotice(outcome: Awaited<ReturnType<ApiClient['queueCampaign']>>): {
  readonly tone: 'ok' | 'danger'
  readonly text: string
} {
  switch (outcome.status) {
    case 'queued':
      return { tone: 'ok', text: `Queued ${count(outcome.queued)} messages. Sending has started.` }

    case 'already-queued':
      return {
        tone: 'ok',
        text: `That campaign was already queued — ${count(outcome.queued)} messages are on their way.`,
      }

    case 'empty-audience':
      return {
        tone: 'danger',
        text: 'Nobody matches that audience right now, so nothing was queued. The campaign is still a draft.',
      }

    case 'over-allowance':
      return {
        tone: 'danger',
        // Both numbers, because "you are over your limit" leaves somebody
        // unable to tell whether they need a bigger plan or a smaller segment.
        text: `This campaign needs ${count(outcome.needed)} sends and your plan has ${count(outcome.remaining)} left this month, so nothing was queued. The allowance starts again on the first — ask us to move you up a plan if you need it sooner.`,
      }

    case 'not-found':
      return { tone: 'danger', text: 'That campaign no longer exists.' }
  }
}
